import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import { useMutation } from "@apollo/client";
import { SEND_OTP, VERIFY_OTP } from "../graphql/queries";

const OtpModal = ({
  isOpen,
  onRequestClose,
  eventId,
  mobile,
  email,
  role,
  onVerify,
}) => {
  const [newOtp, setNewOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(20);
  const [resendCount, setResendCount] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [error, setError] = useState(null);
  const [otpSended, setOtpSended] = useState("");

  const [sendOTP] = useMutation(SEND_OTP);
  const [verifyOTP] = useMutation(VERIFY_OTP);

  useEffect(() => {
    let timer;
    if (isResendDisabled && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [isResendDisabled, resendTimer]);

  useEffect(() => {
    if (eventId && (mobile || email)) {
      // Determine OTP type and send accordingly
      const variables = mobile
        ? { mobile, eventId, role }
        : { email, eventId, role };

      // Send OTP via GraphQL mutation
      sendOTP({
        variables,
      })
        .then((response) => {
          if (response.data?.sendOTP?.success) {
            const recipient = mobile || email;
            setOtpSended(`OTP sent successfully to ${recipient}.`);
          } else {
            setError("Failed to send OTP. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Error sending OTP:", error);
          setError("Failed to send OTP. Please try again.");
        });
    }
  }, [eventId, mobile, email, role, sendOTP]);

  const handleChange = (e) => {
    setNewOtp(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newOtp || newOtp.length !== 5) {
      setError("Please enter a valid 5-digit OTP");
      return;
    }

    try {
      const variables = mobile
        ? { mobile, otp: newOtp }
        : { email, otp: newOtp };

      const response = await verifyOTP({
        variables,
      });

      if (response.data?.verifyOTP?.success) {
        onVerify(true); // Notify parent component of success
        onRequestClose();
      } else {
        setError(
          response.data?.verifyOTP?.message || "Invalid OTP. Please try again."
        );
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError("Failed to verify OTP. Please try again.");
    }
  };

  const handleResend = () => {
    if (resendCount < 3) {
      setResendCount((prev) => prev + 1);
      setResendTimer(20);
      setIsResendDisabled(true);

      // Resend OTP via GraphQL mutation
      const variables = mobile
        ? { mobile, eventId, role }
        : { email, eventId, role };

      sendOTP({
        variables,
      })
        .then((response) => {
          if (response.data?.sendOTP?.success) {
            const recipient = mobile || email;
            setOtpSended(`OTP sent again successfully to ${recipient}.`);
            setError(null);
          } else {
            setError("Failed to resend OTP. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Error resending OTP:", error);
          setError("Failed to resend OTP. Please try again.");
        });
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onRequestClose}
      aria-labelledby="otp-modal-title"
      aria-describedby="otp-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography id="otp-modal-title" variant="h6" component="h2">
          Enter OTP
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="OTP"
            type="number"
            value={newOtp}
            onChange={handleChange}
            error={!!error}
            helperText={error}
          />
          <Button type="submit" variant="contained" color="primary">
            Submit
          </Button>
        </form>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleResend}
          disabled={isResendDisabled || resendCount >= 3}
          sx={{ mt: 2 }}
        >
          {isResendDisabled ? `Resend OTP (${resendTimer}s)` : "Resend OTP"}
        </Button>
        {resendCount >= 3 && (
          <Typography color="error" sx={{ mt: 2 }}>
            Maximum resend attempts reached.
          </Typography>
        )}
        {otpSended && (
          <Typography color={"success"} sx={{ mt: 2 }}>
            {otpSended}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default OtpModal;
